import { ref, nextTick } from 'vue'

/**
 * Composable for calculating dropdown/tooltip positions relative to a trigger element
 * @param {Object} options - Configuration options
 * @param {string} options.position - Position: 'top', 'bottom', 'left', 'right', 'auto'
 * @param {number} options.gap - Gap between trigger and dropdown (default: 8)
 * @param {number} options.viewportPadding - Padding from viewport edges (default: 8)
 * @param {Function} options.customPosition - Custom positioning function
 * @returns {Object} Position calculation utilities
 */
export function useDropdownPosition(options = {}) {
  const {
    position = 'bottom',
    gap = 8,
    viewportPadding = 8,
    customPosition = null
  } = options

  const dropdownStyle = ref({})

  /**
   * Calculate position for dropdown/tooltip
   * @param {HTMLElement} triggerElement - The trigger element
   * @param {HTMLElement} dropdownElement - The dropdown/tooltip element
   * @param {Object} overrides - Override options for this calculation
   */
  const calculatePosition = async (triggerElement, dropdownElement, overrides = {}) => {
    if (!triggerElement || !dropdownElement) return

    await nextTick()

    const triggerRect = triggerElement.getBoundingClientRect()
    const dropdownRect = dropdownElement.getBoundingClientRect()
    const finalGap = overrides.gap ?? gap
    const finalPadding = overrides.viewportPadding ?? viewportPadding
    const finalPosition = overrides.position ?? position

    let top = 0
    let left = 0

    // Use custom positioning if provided
    if (customPosition && typeof customPosition === 'function') {
      const customPos = customPosition(triggerRect, dropdownRect, finalGap)
      top = customPos.top
      left = customPos.left
    } else {
      // Standard positioning logic
      switch (finalPosition) {
        case 'top':
          top = triggerRect.top - dropdownRect.height - finalGap
          left = triggerRect.left + (triggerRect.width / 2) - (dropdownRect.width / 2)
          break
        case 'bottom':
          top = triggerRect.bottom + finalGap
          left = triggerRect.left + (triggerRect.width / 2) - (dropdownRect.width / 2)
          break
        case 'left':
          top = triggerRect.top + (triggerRect.height / 2) - (dropdownRect.height / 2)
          left = triggerRect.left - dropdownRect.width - finalGap
          break
        case 'right':
          top = triggerRect.top + (triggerRect.height / 2) - (dropdownRect.height / 2)
          left = triggerRect.right + finalGap
          break
        case 'auto': {
          // Auto-position: try bottom first, then top if not enough space
          const spaceBelow = window.innerHeight - triggerRect.bottom
          const spaceAbove = triggerRect.top
          if (spaceBelow >= dropdownRect.height + finalGap || spaceBelow > spaceAbove) {
            top = triggerRect.bottom + finalGap
          } else {
            top = triggerRect.top - dropdownRect.height - finalGap
          }
          left = triggerRect.left + (triggerRect.width / 2) - (dropdownRect.width / 2)
          break
        }
        default:
          top = triggerRect.bottom + finalGap
          left = triggerRect.left + (triggerRect.width / 2) - (dropdownRect.width / 2)
      }
    }

    // Ensure dropdown stays within viewport
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Constrain horizontal position
    if (left < finalPadding) left = finalPadding
    if (left + dropdownRect.width > viewportWidth - finalPadding) {
      left = viewportWidth - dropdownRect.width - finalPadding
    }

    // Constrain vertical position
    if (top < finalPadding) top = finalPadding
    if (top + dropdownRect.height > viewportHeight - finalPadding) {
      // If there's not enough space, try positioning above
      if (finalPosition === 'bottom' || finalPosition === 'auto') {
        const spaceAbove = triggerRect.top - finalPadding
        if (spaceAbove >= dropdownRect.height + finalGap) {
          top = triggerRect.top - dropdownRect.height - finalGap
        }
      }
    }

    dropdownStyle.value = {
      top: `${top}px`,
      left: `${left}px`,
      transform: 'none'
    }

    return dropdownStyle.value
  }

  return {
    dropdownStyle,
    calculatePosition
  }
}

